<template>
    <div class="modal upgrade-pro-modal">
        <div
            class="modal-content"
            :class="{'upgrade-modal-content-success': 2 === step }"
        >
            <transition
                name="fade"
            >
                <div
                    v-if="showPopUp"
                    class="pop-up-background"
                />
            </transition>
            <div
                class="modal-close"
                @click="closeModal"
            >
                <i class="far fa-times" />
            </div>
            <template v-if="step === 1">
                <h2>Update Your Fusebox Pro Plan</h2>
                <div class="upgrade-container flex">
                    <div class="plan-details">
                        <p class="section-title">
                            Your Current Plan
                            <span
                                class="cancel-link"
                                @click="goToOffboarding"
                            >
                                (Cancel Pro Plan)
                            </span>
                        </p>
                        <billing-plans
                            :upgrade-pro="true"
                            @plan-changed="updatePlan"
                            @plan-updated="updatePlanDetails"
                        />
                        <transition
                            name="fade"
                            mode="out-in"
                        >
                            <template
                                v-if="!cardForm"
                                >
                                <card-box
                                    :key="'cardBox'"
                                    :upgrade-option="true"
                                    @switch-to-upgrade="handleCardForm"
                                />
                            </template>
                            <template v-else>
                                <card-form
                                    :key="'cardForm'"
                                    :skip-registration="true"
                                    :upgrade-option="true"
                                    :update="true"
                                    @switch-to-card-form="handleCardForm"
                                />
                            </template>
                        </transition>
                    </div>
                    <div class="payment-details">
                        <template
                            v-if="lastSubscriptionDetails && activePlanDetails"
                        >
                            <p class="section-title">
                                Payment Overview
                            </p>
                            <div
                                class="overview-line upgrade-line line-with-buttons"
                                :class="{'seats-error': seatsError }"
                            >
                                <p class="upgrade-label">
                                    # of sites
                                </p>
                                <div class="input">
                                    <div
                                        class="number-handler"
                                        @click="decreaseSites"
                                    >
                                        <i class="far fa-minus" />
                                    </div>
                                    <input
                                        :value="activeSites"
                                        :style="{ width: inputWidth }"
                                        class="counter"
                                        type="text"
                                        @input="activeSites = $event.target.value"
                                    >
                                    <div
                                        class="number-handler"
                                        @click="increaseSites"
                                    >
                                        <i class="far fa-plus" />
                                    </div>
                                </div>
                            </div>
                            <span
                                v-if="seatsError"
                                class="overview-line-error-message"
                            >
                                You have active sites in your account!<br> Please deactivate sites first.
                            </span>
                            <div
                                v-if="licenses"
                                class="overview-line per-site-line upgrade-line"
                            >
                                <p class="upgrade-label">
                                    Price per site
                                </p>
                                <div
                                    class="input line-site-price"
                                >
                                    <template v-if="!userSubscriptionDetails.couponCode">
                                        <span class="site-price">
                                            {{ activePlanDetails | billingPlanFilter }}</span> <span class="site-price-suffix"> / mo
                                        </span>
                                    </template>
                                    <template v-else>
                                    <span class="site-price">
                                        <strike>{{ activeBasePlan | billingPlanFilter }}</strike>
                                        {{ activePlanDetails | billingPlanFilter }}</span> <span class="site-price-suffix"> / mo
                                    </span>
                                    </template>
                                </div>
                            </div>
                            <div
                                v-if="licenses"
                                class="overview-line upgrade-line old-site-price"
                            >
                                <p class="upgrade-label">
                                    Your current monthly payment
                                </p>
                                <div
                                    class="input"
                                >
                                    <span
                                        v-if="lastSubscriptionDetails"
                                        class="site-price"
                                    >
                                        {{ lastSubscriptionDetails | billingPlanFilter }}
                                    </span>
                                </div>
                            </div>
                            <div
                                v-if="licenses"
                                class="overview-line upgrade-line"
                            >
                                <p class="upgrade-label">
                                    Your new {{ toLower( activePlanDetails.nickname ) }} payment
                                </p>
                                <div
                                    class="input"
                                >
                                    <span class="site-price">{{ totalPrice( activePlanDetails.amount ) }}</span>
                                </div>
                            </div>
                            <div v-if="displayPlanInfoBox && ! formProcessing" class="plan-info-box">
                                <p v-if="activePlanDetails.nickname !== userSubscriptionDetails.nickname">
                                    <span>
                                        You’re switching from {{ userSubscriptionDetails.nickname }} to {{ activePlanDetails.nickname }} Plan.
                                    </span>
                                </p>
                                <p>You’ll be charged <span>{{ usdFormatter( presumedInvoice.amount_due ) }}</span> <span>{{ date( presumedInvoice.next_payment_attempt ) }}</span> and then <span>{{ totalPrice( activePlanDetails.amount ) }}</span> each {{ activePlanDetails.interval }}.</p>
                            </div>
                            <div
                                v-if="! userCard && ! formProcessing"
                                class="card-error-box"
                            >
                                <p>
                                    Please insert Credit Card details to update your subscription
                                </p>
                            </div>
                            <base-submit-button
                                :disabled="disabled || !userCard"
                                class="btn"
                                :loading="formProcessing"
                                @click="processForm"
                            >
                                Save Changes to Your Plan
                            </base-submit-button>
                            <div class="cancel-container">
                                <a href="#" @click.prevent="closeModal">
                                    Cancel changes
                                </a>
                            </div>
                        </template>
                    </div>
                </div>
            </template>
            <div
                v-else
                class="upgrade-success"
            >
                <svg-vue class="upgrade-success-image" icon="register-success" />
                <h2>Success!</h2>
                <p>Changes to your Fusebox Pro plan has been saved.</p>
                <base-submit-button
                    class="btn"
                    handle-enter="true"
                    @click="closeModal"
                >
                    Close
                </base-submit-button>
            </div>
        </div>
    </div>
</template>

<script>
import { debounce, toLower } from 'lodash';
import moment from 'moment';
import { mapMutations, mapState, mapGetters, mapActions } from 'vuex';
import { billingPlanFilter } from '@/utils/filters';
import usdFormatter from '@/utils/usdFormatter';
import CardBox from './CardBox';
import apiCall from '@/utils/apiCall';
import toasts from '@/mixins/toasts';
import modal from '@/mixins/modal';
import BillingPlans from '@/components/Register/BillingPlans.vue';
import CardForm from '@/components/Register/CardForm.vue';

export default {
    components: {
        CardBox,
        BillingPlans,
        CardForm,
    },

    mixins: [toasts, modal],

    data: () => ({
        activePlanId: null,
        activePlanDetails: null,
        activeBasePlan: null,
        activeSites: 1,
        sitePrice: '',
        formProcessing: false,
        step: 1,
        planChanged: false,
        lastSubscriptionDetails: false,
        seatsError: false,
        disabled: true,
        displayPlanInfoBox: false,
        firstLoad: true,
        showPopUp: false,
        cardForm: false,
    }),

    computed: {
        ...mapState([
            'user',
            'userSubscription',
            'licenses',
            'userSubscription',
            'presumedInvoice',
        ]),

        ...mapGetters([
            'userSubscriptionDetails',
            'totalLicenseSeats',
            'userCard',
        ]),

        /**
         * Calculates input width
         *
         * @returns { string } Input width value
         */
        inputWidth() {
            const sitesLength = this.activeSites.toString().length;

            return (sitesLength + 1) + 'ch';
        },
    },

    watch: {
        /**
         * Active sites change watcher
         */
        activeSites() {
            if ('' === this.activeSites || '0' === this.activeSites) {
                this.disabled = true;
                this.seatsError = true;
                return;
            }

            if (this.totalLicenseSeats.used <= this.activeSites) {
                if (1 < this.activeSites) {
                    this.seatsError = false;
                    this.getUpcomingInvoiceInfo();
                    this.calculatePricePerSite();
                }
            } else {
                this.seatsError = true;
            }
        },
    },

    /**
     * Created hook
     *
     */
    created() {
        this.activeSites = this.userSubscription.quantity;

        this.loadLastPlan();
    },

    /**
     * Update hook
     */
    updated() {
        this.loadLastPlan();
    },

    methods: {
        ...mapActions(
            [
                'loadInvoices',
                'loadLicenses',
                'loadUpcomingInvoice',
                'loadUserSubscription',
                'updateUser',
            ]
        ),

        ...mapMutations(['changeUIState', 'savePresumedInvoice']),

        usdFormatter,
        toLower,

        /**
         * Loads last  user plan
         */
        loadLastPlan() {
            if (! this.planChanged) {
                this.lastSubscriptionDetails = this.userSubscriptionDetails;
            }
        },

        /**
         * Closes upgrade modal
         *
         */
        closeModal() {
            this.changeUIState({ isUpgradeProModalVisible: false });
        },

        /**
         * Increse sites counter
         *
         */
        increaseSites() {
            this.seatsError = false;

            this.activeSites++;
            this.getUpcomingInvoiceInfo();
            this.calculatePricePerSite();
        },

        /**
         * Decrease sites counter
         *
         */
        decreaseSites() {
            this.seatsError = false;

            if (this.totalLicenseSeats.used < this.activeSites) {
                if (1 < this.activeSites) {
                    this.activeSites--;
                    this.getUpcomingInvoiceInfo();
                    this.calculatePricePerSite();
                }
            } else {
                this.seatsError = true;
            }
        },

        /**
         * Handle plan change
         *
         * @param {string} id Plan key.
         */
        updatePlan(id) {
            this.activePlanId = id;
        },

        /**
         * Handle plan change
         *
         * @param { object } plan details
         *
         */
        updatePlanDetails(plan) {
            this.activePlanDetails = {
                ...plan,
                amount: this.calculateAmount(plan.amount),
            };

            this.activeBasePlan = plan;

            this.calculatePricePerSite();
            this.getUpcomingInvoiceInfo();
            this.firstLoad = false;
        },

        /**
         * Calculate amount using subscription coupon if applied.
         *
         * @param  {number} amount Amount.
         * @returns {number}  Calculated amount.
         */
        calculateAmount(amount) {
            const { couponAmount, couponPercent } = this.userSubscriptionDetails;

            if (couponPercent) {
                amount -= Math.round(amount * couponPercent / 100, 2);
            } else if (couponAmount) {
                amount -= couponAmount;
            }

            return amount;
        },

        /**
         * Calculates discount on plan
         *
         */
        calculatePricePerSite() {
            if (this.lastSubscriptionDetails.nickname) {
                const plan = { ...this.activePlanDetails };

                if (! this.lastSubscriptionDetails.couponPercent) {
                    plan.amount = plan.amount * this.activeSites;

                    this.sitePrice = billingPlanFilter(plan);
                } else {
                    plan.amount = plan.amount * this.activeSites;
                    plan.amount = (plan.amount * ((100 - this.lastSubscriptionDetails.couponPercent) / 100));

                    this.sitePrice = billingPlanFilter(plan);
                }
            }
        },

        /**
         * Process user form
         *
         */
        async processForm() {
            this.formProcessing = true;

            const data = {
                plan: this.activePlanId,
                quantity: this.activeSites,
                payment_method: this.userSubscription.default_payment_method,
                coupon: this.userSubscription.discount?.coupon?.id || null,
            };

            let
                method = 'put';
            let url = `/api/billing/subscriptions/${this.userSubscription.dbId}`;
            let successStatus = 200;

            if ('trialing' === this.userSubscription.status) {
                method = 'post';
                url = '/api/billing/subscriptions';
                successStatus = 201;
                data.replace = this.userSubscription.dbId;
            }

            const { status } = await apiCall(this, method, url, data);

            this.formProcessing = true;

            if (successStatus === status) {
                this.planChanged = true;

                await this.updateUser();

                const subscription = this.loadUserSubscription({ force: true });
                const licenses = this.loadLicenses(true);
                const invoice = this.loadUpcomingInvoice();
                const invoices = this.loadInvoices({ force: true });

                Promise.all([
                    invoice,
                    invoices,
                    licenses,
                    subscription,
                ])
                    .then(() => { this.step = 2 })
                    .catch(() => this.toastError({
                        message: {
                            title: 'We couldn\'t update your Fusebox Pro Plan. Please try again later or contact us.',
                        },
                    }))
                    .finally(() => { this.formProcessing = false });
            } else {
                this.formProcessing = false;
            }
        },

        /**
         * Calculate total price per plan
         *
         * @param   {number}  amount  Plan cost.
         *
         * @returns  {string}          Plan total price.
         */
        totalPrice(amount) {
            const priceTotal = amount * this.activeSites;

            const formattedPrice = usdFormatter(priceTotal);

            return formattedPrice;
        },

        /**
         * Formats date
         *
         * @param {number} date Date timestamp
         *
         * @returns {string} Formated date
         */
        date(date) {
            const momentDate = moment.unix(date);

            return momentDate.isSame(new Date(), 'day') || null === date
                ? 'today' : momentDate.format('MMM D, YYYY');
        },

        /**
         * Get presumed invoice info if plan or sites quantity changed.
         *
         * @returns {void}
         */
        getUpcomingInvoiceInfo() {
            if (null === this.activePlanDetails || this.firstLoad) {
                return;
            }

            if (
                this.userSubscription.quantity === this.activeSites &&
                this.userSubscriptionDetails.nickname === this.activePlanDetails.nickname
            ) {
                this.displayPlanInfoBox = false;
                return;
            }

            this.disabled = true;
            this.formProcessing = true;

            this.getUpcomingInvoice();
        },

        /**
         * Gets upcoming invoice data
         */
        getUpcomingInvoice: debounce(function () {
            const data = {
                quantity: this.activeSites,
                plan: this.activePlanId,
                presumed: true,
            };

            this.formProcessing = true;

            this.loadUpcomingInvoice(data)
                .then(() => { this.displayPlanInfoBox = true })
                .catch(() => { this.displayPlanInfoBox = false })
                .finally(() => {
                    this.formProcessing = false;
                    this.disabled = false;
                });
        }, 500),

        /**
         * Handles upgrade card form
         */
        handleCardForm() {
            this.cardForm = ! this.cardForm;
        },

        /**
         * Navigates user to offboarding view
         */
        goToOffboarding() {
            this.closeModal();
            this.$router.push({ path: '/offboarding' });
        },

    },
};
</script>
